import { cloneDeep } from 'lodash-es';
import { type JSX, useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useParams } from 'react-router';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { DueDiligenceAssessmentConfigurationApi } from 'Api/TPRM/DueDiligenceAssessmentConfigurationApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { UcrmAssessmentApi } from 'Api/UCRM/UcrmAssessmentApi';
import { Button, Link } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { DueDiligenceWorkflow } from 'Components/DueDiligenceWorkflow/DueDiligenceWorkflow';
import { ExceptionsListing, ExceptionsListingProps } from 'Components/Exceptions/ExceptionsListing/ExceptionsListing';
import { FoldersTable, FoldersTableProps } from 'Components/Folders/FoldersTable/FoldersTable';
import { CircleIndicator } from 'Components/Indicator/CircleIndicator';
import { IssuesListing, IssuesListingProps } from 'Components/Issues/IssuesListing/IssuesListing';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDashboard } from 'Components/PageLayout/PageLayoutDashboard';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { TagListing } from 'Components/Tags/TagListing/TagListing';
import { Text } from 'Components/Text/Text';
import { ArchivedDueDiligenceAssessmentsModal } from 'Components/UcrmTprm/ArchivedDueDiligenceAssessmentsModal/ArchivedDueDiligenceAssessmentsModal';
import { ArchivedDueDiligenceWorkflowsTable } from 'Components/UcrmTprm/ArchivedDueDiligenceWorkflowsTable/ArchivedDueDiligenceWorkflowsTable';
import { AssessmentsListing } from 'Components/UcrmTprm/AssessmentsListing/AssessmentsListing';
import { CommonAssessment } from 'Components/UcrmTprm/CommonAssessment/CommonAssessment';
import { CommonAssessmentSelectionModal, CommonAssessmentSelectionModalProps } from 'Components/UcrmTprm/CommonAssessmentSelectionModal/CommonAssessmentSelectionModal';
import { ConfirmUnlinkAssessmentSubjectsModal, ConfirmUnlinkAssessmentSubjectsModalProps } from 'Components/UcrmTprm/ConfirmUnlinkAssessmentSubjectsModal/ConfirmUnlinkAssessmentSubjectsModal';
import { DueDiligenceWorkflowDueDateModal } from 'Components/UcrmTprm/DueDiligenceWorkflowDueDateModal/DueDiligenceWorkflowDueDateModal';
import { DueDiligenceWorkflowFilesModal } from 'Components/UcrmTprm/DueDiligenceWorkflowFilesModal/DueDiligenceWorkflowFilesModal';
import { IrqTargetCompletionDateModal } from 'Components/UcrmTprm/IrqTargetCompletionDateModal/IrqTargetCompletionDateModal';
import { IrqTargetCompletionDateSelection, getDefaultIrqTargetCompletionDate, getIrqTargetCompletionDateSelectionProps } from 'Components/UcrmTprm/IrqTargetCompletionDateSelection/IrqTargetCompletionDateSelection';
import { UsersModal } from 'Components/UcrmTprm/UsersModal/UsersModal';
import { UNAUTHORIZED_MESSAGE } from 'Config/Errors';
import { ICON_DELETE_REMOVE, ICON_EDIT_MODIFY_UPDATE, ICON_RESTART, ICON_SUBMIT } from 'Config/Icons';
import { UCRM_BUSINESSES_AND_USE_CASES_LISTING, UCRM_UPDATE_BUSINESS, UCRM_UPDATE_USE_CASE, UCRM_USE_CASE_DASHBOARD } from 'Config/PathTemplates';
import { AuthHandler } from 'Helpers/Auth/AuthHandler';
import { isForbiddenResponseError } from 'Helpers/Auth/ResponseUtil';
import { iso8601ToUsDateLong, jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { AssessmentWorkflowState, DueDiligenceWorkflowFileWithSource } from 'Models/DueDiligenceWorkflow';
import { ExceptionBreadcrumbSource, ExceptionResponse } from 'Models/Exceptions';
import { FolderBreadcrumbSource, FolderVersion } from 'Models/Folders';
import { InherentRiskQuestionnaireResponse, irqQuestionIsAnswered } from 'Models/InherentRisk';
import { IssueBreadcrumbSource, IssueResponse } from 'Models/Issues';
import { getEffectivenessVariantColor, numberAsEffectiveness, numberAsEffectivenessString } from 'Models/OperationalControls';
import { ActiveState } from 'Models/Organization';
import { ProductModule } from 'Models/ProductModule';
import { RiskRating, getRiskRatingVariantColor, numberAsRiskRatingString, numberAsRiskScore, riskRatingAsString } from 'Models/RiskRating';
import { BusinessResponse, UcrmDueDiligenceAssessment, UcrmDueDiligenceWorkflowArchive, UseCase } from 'Models/Ucrm';
import { AssessmentsDisplayInfo } from 'Models/UcrmTprm';

import { AssociatedTprmServicesListing } from './AssociatedTprmServicesListing/AssociatedTprmServicesListing';
import { InviteRespondentsModal } from './InviteRespondentsModal/InviteRespondentsModal';
import styles from './UseCaseDashboard.module.css';

/** URL search params that this page appends to its Manage Business and Manage Use Case links, so that those pages can include a breadcrumb back to this page. */
export const SEARCH_PARAM_USE_CASE_DASHBOARD_ORIGIN_USE_CASE_ID = 'useCaseId';
export const SEARCH_PARAM_USE_CASE_DASHBOARD_ORIGIN_USE_CASE_NAME = 'useCaseName';

/**
 * How much of a use case's inherent risk questionnaire has been answered.
 * Questions in sections that are not applicable count as answered.
 * @param numberOfQuestions The number of questions in all sections, including sections that are not applicable.
 * @param numberOfQuestionsAnswered The number of answered questions in applicable sections, plus the number of questions in sections that are not applicable.
 */
export interface InherentRiskQuestionnaireProgress {
    numberOfQuestions: number;
    numberOfQuestionsAnswered: number;
}

enum PageElements {
    AssessmentHistoryModal,
    AssessmentFilesModal,
    ConfirmStartNewWorkflowModal,
    ConfirmRestartWorkflowModal,
    ConfirmClearWorkflowModal,
    CommonAssessmentSelectionModal,
    DelegatesModal,
    InviteRespondentsModal,
    SetAssessmentDueDate,
    SetIrqTargetCompletionDate,
    UnlinkCommonAssessmentModal,
    None,
}

interface UrlParams {
    service_id: string;
    third_party_id: string;
}

export interface UseCaseDashboardProps {
    authHandler: AuthHandler;
    documentApi: DocumentApi;
    dueDiligenceAssessmentConfigurationApi: DueDiligenceAssessmentConfigurationApi;
    ucrmApi: ClientUcrmApi;
    ucrmAssessmentApi: UcrmAssessmentApi;
}

/**
 * The dashboard for a single use case.
 * Shows the business and use case details, the due diligence workflow and its history, the associated TPRM third-party services, and the business's issues, exceptions and folders.
 * React Router reuses this component when the user navigates to another use case's dashboard (e.g., via a shared due diligence assessments link), so the use case is reset when the `service_id` URL param changes.
 */
export const UseCaseDashboard = (props: UseCaseDashboardProps): JSX.Element => {
    const { service_id: useCaseIdUrlParam, third_party_id: businessIdUrlParam } = useParams<keyof UrlParams>() as UrlParams;

    const cachedData = useCachedData();

    const [assessments, setAssessments] = useState<UcrmDueDiligenceAssessment[]>();
    const [folders, setFolders] = useState<FolderVersion[]>();
    const [folderTypes, setFolderTypes] = useState<string[]>();
    const [irqTargetCompletionInDays, setIrqTargetCompletionInDays] = useState<number | null>(); // Will contain `null` if the request for data has completed, but no data exists.
    const [irqProgress, setIrqProgress] = useState<InherentRiskQuestionnaireProgress | null>(); // Will contain `null` if the request for data has completed, but no data exists.
    const [useCaseResponse, setUseCaseResponse] = useState<UseCase>();
    const [businessResponse, setBusinessResponse] = useState<BusinessResponse>();
    const [workflowArchives, setWorkflowArchives] = useState<UcrmDueDiligenceWorkflowArchive[]>();
    const [ucrmAccessDenied, setUcrmAccessDenied] = useState<boolean>();
    const [zeroStateText, setZeroStateText] = useState<string>();
    const [displayedElement, setDisplayedElement] = useState<PageElements>(PageElements.None);
    // Has a value after the user chooses to view all assessments from a specific historical assessment. Assessments are displayed in a modal.
    const [displayedWorkflowArchive, setDisplayedWorkflowArchive] = useState<{ workflowArchive: UcrmDueDiligenceWorkflowArchive }>();
    // Has a value after the user chooses to view all files from a specific historical assessment. Files are displayed in a modal.
    const [displayedWorkflowArchiveFiles, setDisplayedWorkflowArchiveFiles] = useState<{ workflowArchive: UcrmDueDiligenceWorkflowArchive; categorizedFiles: DueDiligenceWorkflowFileWithSource[] }>();
    const [selectedItem, setSelectedItem] = useState<IssueResponse | ExceptionResponse | UseCase>();
    const [selectedIrqTargetCompletionDate, setSelectedIrqTargetCompletionDate] = useState<Date>();

    const assessmentsDisplayInfo: AssessmentsDisplayInfo<UcrmDueDiligenceAssessment> | undefined = useMemo(() => {
        if (assessments === undefined || useCaseResponse === undefined) {
            return undefined;
        }

        return useCaseResponse.assessment_workflow_setup.common_assessment_parent ? { usingCommonAssessments: true, parentSummary: useCaseResponse.assessment_workflow_setup.common_assessment_parent } : { usingCommonAssessments: false, assessments };
    }, [assessments, useCaseResponse]);

    const getAssessments = useCallback(async (): Promise<void> => {
        try {
            const response = await props.ucrmAssessmentApi.getAllAssessmentsForAssessmentSubject(businessIdUrlParam, useCaseIdUrlParam);
            setAssessments(response.data);
        } catch (error) {
            if (isForbiddenResponseError(error)) {
                setUcrmAccessDenied(true);
            } else {
                handleRequestError(error);
            }
        }
    }, [props.ucrmAssessmentApi, businessIdUrlParam, useCaseIdUrlParam]);

    const getInherentRiskQuestionnaireTargetCompletion = useCallback(async () => {
        try {
            const response = await props.dueDiligenceAssessmentConfigurationApi.getTimeframes();
            setIrqTargetCompletionInDays(response.data.expected_days_to_complete_irq ?? null);
        } catch (error) {
            if (isForbiddenResponseError(error)) {
                setUcrmAccessDenied(true);
            } else {
                handleRequestError(error);
            }
        }
    }, [props.dueDiligenceAssessmentConfigurationApi]);

    const getUseCaseDetails = useCallback(async (): Promise<void> => {
        try {
            const detailedUseCaseResponse = await props.ucrmApi.getUseCaseDetails(businessIdUrlParam, useCaseIdUrlParam);
            setUseCaseResponse(detailedUseCaseResponse.data);
        } catch (error) {
            if (isForbiddenResponseError(error)) {
                setUcrmAccessDenied(true);
            } else {
                handleRequestError(error);
            }
        }
    }, [useCaseIdUrlParam, businessIdUrlParam, props.ucrmApi]);

    const getBusinessDetails = useCallback(async (): Promise<void> => {
        try {
            const detailedBusinessResponse = await props.ucrmApi.getBusinessDetails(businessIdUrlParam);
            setBusinessResponse(detailedBusinessResponse.data);
        } catch (error) {
            if (isForbiddenResponseError(error)) {
                setUcrmAccessDenied(true);
            } else {
                handleRequestError(error);
            }
        }
    }, [props.ucrmApi, businessIdUrlParam]);

    const getFolders = useCallback(async (): Promise<void> => {
        try {
            const response = await props.ucrmApi.getAllCurrentFoldersForBusiness(businessIdUrlParam);
            setFolders(response.data);
        } catch (error) {
            if (isForbiddenResponseError(error)) {
                setUcrmAccessDenied(true);
            } else {
                handleRequestError(error);
            }
        }
    }, [props.ucrmApi, businessIdUrlParam]);

    const getIrqProgress = useCallback(async (): Promise<void> => {
        try {
            const irqResponse = await props.ucrmApi.getInherentRiskQuestionnaire(businessIdUrlParam, useCaseIdUrlParam);
            setIrqProgress(irqResponse.data ? calculateInherentRiskQuestionnaireProgress(irqResponse.data) : null);
        } catch (error) {
            if (isForbiddenResponseError(error)) {
                setUcrmAccessDenied(true);
            } else {
                handleRequestError(error);
            }
        }
    }, [useCaseIdUrlParam, businessIdUrlParam, props.ucrmApi]);

    /**
     * Initial network calls
     */
    useEffect(() => {
        getInherentRiskQuestionnaireTargetCompletion();
        getBusinessDetails();
        getUseCaseDetails();
        getIrqProgress();
        getAssessments();
        getFolders();
    }, [getInherentRiskQuestionnaireTargetCompletion, getUseCaseDetails, useCaseIdUrlParam, businessIdUrlParam, props.ucrmApi, getBusinessDetails, getIrqProgress, getAssessments, getFolders]);

    useEffect(() => {
        if (useCaseResponse && useCaseResponse.id !== useCaseIdUrlParam) {
            /**
             * Reset state when use case id params change.
             * This prop changes when a user clicks a link that redirects them to a different use case dashboard.
             * React Router does not fully re-initialize the component. i.e. state is not re-initialized.
             * Resetting the values causes the placeholder animation to show while network calls are being made and state is being updated.
             * The client details and associated business don't change when navigating between linked use cases. If a user navigates to a use case associated with a different business, the component is re-initialized.
             */
            setUseCaseResponse(undefined);
        }
    }, [useCaseIdUrlParam, useCaseResponse]);

    /** Fetches all past assessments for the use case, to be displayed in the Past Assessments table. */
    useEffect(() => {
        const getWorkflowArchives = async (): Promise<void> => {
            try {
                const response = await props.ucrmAssessmentApi.getAllDueDiligenceWorkflowArchivesForUseCase(businessIdUrlParam, useCaseIdUrlParam);
                setWorkflowArchives(response.data);
            } catch (error) {
                if (isForbiddenResponseError(error)) {
                    setUcrmAccessDenied(true);
                } else {
                    handleRequestError(error);
                }
            }
        };

        getWorkflowArchives();
    }, [businessIdUrlParam, useCaseIdUrlParam, props.ucrmAssessmentApi]);

    /** Fetches all folder types, for use in the filter of the Folders table. */
    useEffect(() => {
        const getFolderTypes = async (): Promise<void> => {
            try {
                const response = await props.ucrmApi.getFolderTypes();
                setFolderTypes(response.data.type_set);
            } catch (error) {
                if (isForbiddenResponseError(error)) {
                    setUcrmAccessDenied(true);
                } else {
                    handleRequestError(error);
                }
            }
        };

        getFolderTypes();
    }, [props.ucrmApi]);

    const startNewAssessmentWorkflow = async (): Promise<string> => {
        await props.ucrmApi.startNewAssessmentWorkflow(businessIdUrlParam, useCaseIdUrlParam, { due_diligence_workflow_due_date: useCaseResponse?.assessment_workflow_setup.due_date, irq_target_completion_date: selectedIrqTargetCompletionDate ? jsDateToIso8601(selectedIrqTargetCompletionDate) : undefined });
        await getUseCaseDetails();
        await getIrqProgress();
        return 'Due diligence workflow started.';
    };

    const restartAssessmentWorkflow = async (): Promise<string> => {
        await props.ucrmApi.restartAssessmentWorkflow(businessIdUrlParam, useCaseIdUrlParam, { due_diligence_workflow_due_date: useCaseResponse?.assessment_workflow_setup.due_date, irq_target_completion_date: selectedIrqTargetCompletionDate ? jsDateToIso8601(selectedIrqTargetCompletionDate) : undefined });
        await getUseCaseDetails();
        await getIrqProgress();
        await getAssessments();
        return 'Due diligence workflow restarted.';
    };

    const clearAssessmentWorkflow = async (): Promise<string> => {
        await props.ucrmApi.clearAssessmentWorkflow(businessIdUrlParam, useCaseIdUrlParam);
        await getUseCaseDetails();
        await getIrqProgress();
        await getAssessments();
        return 'Due diligence workflow cleared.';
    };

    const handleRequestError = (error: Error): void => setZeroStateText(error.message);

    const hideModal = () => {
        setDisplayedElement(PageElements.None);
        setSelectedItem(undefined);
    };

    const handleAssessmentsClicked = (workflowArchive: UcrmDueDiligenceWorkflowArchive) => {
        setDisplayedWorkflowArchive({ workflowArchive });
        setDisplayedElement(PageElements.AssessmentHistoryModal);
    };

    const handleWorkflowArchiveFilesClicked = (workflowArchive: UcrmDueDiligenceWorkflowArchive, categorizedFiles: DueDiligenceWorkflowFileWithSource[]) => {
        setDisplayedWorkflowArchiveFiles({ workflowArchive, categorizedFiles });
        setDisplayedElement(PageElements.AssessmentFilesModal);
    };

    const commonAssessmentSelectionModalProps = (useCase: UseCase): CommonAssessmentSelectionModalProps => {
        return {
            module: ProductModule.UCRM,
            moduleApi: props.ucrmApi,
            assessmentSubject: useCase,
            hideModal: hideModal,
            onComplete: getUseCaseDetails,
        };
    };

    const confirmUnlinkUseCasesModalProps = (useCase: UseCase): ConfirmUnlinkAssessmentSubjectsModalProps => {
        return {
            moduleApi: props.ucrmApi,
            assessmentSubject: useCase,
            hideModal: hideModal,
            onComplete: getUseCaseDetails,
        };
    };

    /** Determine which internal contact information to display, based on how many delegates there are. */
    const renderUseCaseDelegates = (): JSX.Element | JSX.Element[] => {
        // If there are no delegates, display a dash.
        if (!useCaseResponse || useCaseResponse.delegates.length === 0) {
            return (
                <Text variant="Text2" color="white">
                    -
                </Text>
            );
        }

        // If there are is one delegate, display their name.
        else if (useCaseResponse.delegates.length === 1) {
            return (
                <Text variant="Text2" color="white">
                    {getUserNameFromSubject(useCaseResponse.delegates[0], cachedData.users, UserNameFormat.FIRST_SPACE_LAST)}
                </Text>
            );
        }

        // If there is more than one delegate, display a link to a modal that contains all the delegates' info.
        else {
            return (
                <Button variant="linkText" size="lg" onClick={() => setDisplayedElement(PageElements.DelegatesModal)}>
                    {`Show ${useCaseResponse.delegates.length} Delegates`}
                </Button>
            );
        }
    };

    /** Renders the contents of the business and use case information dashlet. */
    const renderBusinessAndUseCaseInformationDashlet = (business: BusinessResponse, useCase: UseCase): JSX.Element => {
        return (
            <div className={styles.smallSection}>
                <Text variant="Text4" color="darkGray" noStyles toUppercase>
                    Use Case Description
                </Text>
                <Text variant="Text2" color="white">
                    {useCase.description}
                </Text>
                <Text variant="Text4" color="darkGray" noStyles toUppercase>
                    Shared Due Diligence Assessments
                </Text>
                <CommonAssessment assessmentSubject={useCase} module={ProductModule.UCRM} onSelectLinkToParent={onSelectLinkToParent} onSelectUnlinkFromParent={onSelectUnlinkFromParent} />
                <Text variant="Text4" color="darkGray" noStyles toUppercase>
                    Business Tags
                </Text>
                {business.tags.length > 0 ? (
                    <TagListing tags={cachedData.convertTagIdsToDisplayableTags(business.tags)} />
                ) : (
                    <Text variant="Text2" color="white">
                        -
                    </Text>
                )}
                <Text variant="Text4" color="darkGray" noStyles toUppercase>
                    Use Case Tags
                </Text>
                {useCase.tags.length > 0 ? (
                    <TagListing tags={cachedData.convertTagIdsToDisplayableTags(useCase.tags)} />
                ) : (
                    <Text variant="Text2" color="white">
                        -
                    </Text>
                )}
            </div>
        );
    };

    if (ucrmAccessDenied) {
        return <Text>{UNAUTHORIZED_MESSAGE}</Text>;
    }

    if (zeroStateText) {
        return <Text>{zeroStateText}</Text>;
    }

    // The use case IDs are compared to prevent any stale data being shown when the use case ID URL parameter changes.
    if (!(useCaseResponse && useCaseResponse.id === useCaseIdUrlParam && businessResponse && workflowArchives && folders && folderTypes && irqProgress !== undefined && irqTargetCompletionInDays !== undefined && assessmentsDisplayInfo !== undefined)) {
        return <Placeholder />;
    }

    const populateRecommendedIrqTargetCompletionDateAsDefault = () =>
        setSelectedIrqTargetCompletionDate(
            getDefaultIrqTargetCompletionDate({
                action: 'startServiceAssessmentWorkflow',
                currentAssessmentWorkflowIrqTargetCompletionDate: useCaseResponse.assessment_workflow_setup.irq_target_completion_date,
                expectedIrqDurationInDays: irqTargetCompletionInDays ?? undefined,
            })
        );

    const onSelectLinkToParent = () => {
        setSelectedItem(cloneDeep(useCaseResponse));
        setDisplayedElement(PageElements.CommonAssessmentSelectionModal);
    };

    const onSelectUnlinkFromParent = () => {
        setSelectedItem(cloneDeep(useCaseResponse));
        setDisplayedElement(PageElements.UnlinkCommonAssessmentModal);
    };

    const inherent_risk_score = useCaseResponse.inherent_risk_score ?? 0;
    const useCaseTitle = `${useCaseResponse.vendor_name} - ${useCaseResponse.name}${useCaseResponse.active_state === ActiveState.INACTIVE ? ' (Inactive)' : ''}`;
    const assessmentDueDateText = useCaseResponse.assessment_workflow_setup.due_date ? iso8601ToUsDateLong(useCaseResponse.assessment_workflow_setup.due_date) : '-';
    const lastAssessedDateText = useCaseResponse.last_assessed ? iso8601ToUsDateLong(useCaseResponse.last_assessed) : '-';
    const irqTargetCompletionDateText = useCaseResponse.assessment_workflow_setup.irq_target_completion_date ? iso8601ToUsDateLong(useCaseResponse.assessment_workflow_setup.irq_target_completion_date) : '-';
    const inherentRisk = numberAsRiskRatingString(inherent_risk_score);
    const inherentRiskVariant = getRiskRatingVariantColor(numberAsRiskScore(inherent_risk_score));
    const residualRisk = useCaseResponse.residual_risk_score ? numberAsRiskRatingString(useCaseResponse.residual_risk_score) : undefined;
    const residualRiskVariant = useCaseResponse.residual_risk_score ? getRiskRatingVariantColor(numberAsRiskScore(useCaseResponse.residual_risk_score)) : undefined;
    const businessManager = getUserNameFromSubject(businessResponse.vendor_manager_user_id, cachedData.users, UserNameFormat.FIRST_SPACE_LAST)!;
    const useCaseManager = getUserNameFromSubject(useCaseResponse.vendor_service_manager_user_id, cachedData.users, UserNameFormat.FIRST_SPACE_LAST)!;

    const issues = businessResponse.issues;
    const exceptions = businessResponse.exceptions;

    const issuesListingProps: IssuesListingProps = {
        type: ProductModule.UCRM,
        issues: issues,
        documentApi: props.documentApi,
        colorTheme: 'dark',
        createButtonVariant: 'primaryButton',
        createIssueOrigin: {
            source: IssueBreadcrumbSource.ASSESSMENT_SUBJECT_DASHBOARD,
            module: ProductModule.UCRM,
            organizationId: businessResponse.id,
            assessmentSubjectId: useCaseResponse.id,
            assessmentSubjectDisplayName: `${useCaseResponse.vendor_name} - ${useCaseResponse.name}`,
        },
        disableOrganizationFilter: true,
        hideOrganizationInfo: true,
        preselectedOrganizationIdForCreate: businessResponse.id,
        preselectedOrganizationIdForFilter: businessResponse.id,
        organizations: [businessResponse],
    };

    const exceptionsListingProps: ExceptionsListingProps = {
        type: ProductModule.UCRM,
        exceptions: exceptions,
        documentApi: props.documentApi,
        colorTheme: 'dark',
        createButtonVariant: 'primaryButton',
        createExceptionOrigin: {
            source: ExceptionBreadcrumbSource.ASSESSMENT_SUBJECT_DASHBOARD,
            module: ProductModule.UCRM,
            organizationId: businessResponse.id,
            assessmentSubjectId: useCaseResponse.id,
            assessmentSubjectDisplayName: `${useCaseResponse.vendor_name} - ${useCaseResponse.name}`,
        },
        disableOrganizationFilter: true,
        hideOrganizationInfo: true,
        preselectedOrganizationIdForCreate: businessResponse.id,
        preselectedOrganizationIdForFilter: businessResponse.id,
        organizations: [businessResponse],
    };

    const foldersListingProps: FoldersTableProps = {
        colorTheme: 'dark',
        createButtonVariant: 'primaryButton',
        createFolderOrigin: {
            source: FolderBreadcrumbSource.ASSESSMENT_SUBJECT_DASHBOARD,
            module: ProductModule.UCRM,
            organizationId: businessResponse.id,
            assessmentSubjectId: useCaseResponse.id,
            assessmentSubjectDisplayName: `${useCaseResponse.vendor_name} - ${useCaseResponse.name}`,
        },
        documentApi: props.documentApi,
        folders: folders,
        folderTypes: folderTypes,
        hideOrganizationInfo: true,
        module: ProductModule.UCRM,
        showTitleAndCreateButton: true,
        preselectedOrganizationIdForCreate: businessResponse.id,
        organizations: [businessResponse],
    };

    const irqTargetCompletionDatePicker = (
        <IrqTargetCompletionDateSelection
            {...getIrqTargetCompletionDateSelectionProps(
                ProductModule.UCRM,
                {
                    action: 'startServiceAssessmentWorkflow',
                    currentAssessmentWorkflowIrqTargetCompletionDate: useCaseResponse.assessment_workflow_setup.irq_target_completion_date,
                    expectedIrqDurationInDays: irqTargetCompletionInDays ?? undefined,
                },
                setSelectedIrqTargetCompletionDate
            )}
        />
    );

    return (
        <>
            {displayedElement === PageElements.AssessmentHistoryModal && displayedWorkflowArchive && (
                <ArchivedDueDiligenceAssessmentsModal
                    workflowArchive={displayedWorkflowArchive.workflowArchive}
                    module={ProductModule.UCRM}
                    hideModal={() => {
                        setDisplayedElement(PageElements.None);
                        setDisplayedWorkflowArchive(undefined);
                    }}
                />
            )}
            {displayedElement === PageElements.AssessmentFilesModal && displayedWorkflowArchiveFiles && (
                <DueDiligenceWorkflowFilesModal
                    workflowArchive={displayedWorkflowArchiveFiles.workflowArchive}
                    categorizedFiles={displayedWorkflowArchiveFiles.categorizedFiles}
                    documentApi={props.documentApi}
                    hideModal={() => {
                        setDisplayedElement(PageElements.None);
                        setDisplayedWorkflowArchiveFiles(undefined);
                    }}
                />
            )}
            {displayedElement === PageElements.ConfirmStartNewWorkflowModal && (
                <ConfirmationModal operationType={ConfirmationModalOperationType.NON_DESTRUCTIVE} headerText="Start New Due Diligence Workflow" areYouSureText="Are you sure you want to start the use case due diligence workflow?" buttonText="START" buttonLoadingText="Starting..." buttonIcon={ICON_SUBMIT} performOperation={startNewAssessmentWorkflow} hideModal={() => setDisplayedElement(PageElements.None)}>
                    <Text>This will generate a new inherent risk questionnaire based on the latest configuration.</Text>
                    {irqTargetCompletionDatePicker}
                </ConfirmationModal>
            )}
            {displayedElement === PageElements.ConfirmRestartWorkflowModal && (
                <ConfirmationModal operationType={ConfirmationModalOperationType.NON_DESTRUCTIVE} headerText="Restart Due Diligence Workflow" areYouSureText="Are you sure you want to restart the due diligence workflow?" buttonText="RESTART" buttonLoadingText="Restarting..." buttonIcon={ICON_RESTART} performOperation={restartAssessmentWorkflow} hideModal={() => setDisplayedElement(PageElements.None)}>
                    <Text>This will return the workflow to the first step (evaluating inherent risk), and the latest configuration will be applied to the inherent risk questionnaire.</Text>
                    {irqTargetCompletionDatePicker}
                </ConfirmationModal>
            )}
            {displayedElement === PageElements.ConfirmClearWorkflowModal && (
                <ConfirmationModal operationType={ConfirmationModalOperationType.DESTRUCTIVE} headerText="Clear Due Diligence Workflow" areYouSureText="Are you sure you want to clear the due diligence workflow?" buttonText="CLEAR" buttonLoadingText="Clearing..." buttonIcon={ICON_DELETE_REMOVE} performOperation={clearAssessmentWorkflow} hideModal={() => setDisplayedElement(PageElements.None)}>
                    <Text>All responses to the inherent risk questionnaire, due diligence questionnaires, due diligence assessments, and final review will be lost. The inherent risk rating, overall control effectiveness, and residual risk rating for the use case will be cleared.</Text>
                </ConfirmationModal>
            )}
            {displayedElement === PageElements.CommonAssessmentSelectionModal && <CommonAssessmentSelectionModal {...commonAssessmentSelectionModalProps(selectedItem as UseCase)} />}
            {displayedElement === PageElements.UnlinkCommonAssessmentModal && <ConfirmUnlinkAssessmentSubjectsModal {...confirmUnlinkUseCasesModalProps(selectedItem as UseCase)} />}
            {displayedElement === PageElements.DelegatesModal && <UsersModal userIds={useCaseResponse.delegates} header="Use Case Delegates" hideModal={hideModal} />}
            {displayedElement === PageElements.InviteRespondentsModal && <InviteRespondentsModal assessmentApi={props.ucrmAssessmentApi} assessments={assessments ?? []} hideModal={hideModal} onRespondentsUpdated={getAssessments} />}
            {displayedElement === PageElements.SetAssessmentDueDate && <DueDiligenceWorkflowDueDateModal module={ProductModule.UCRM} dueDiligenceAssessmentConfigurationApi={props.dueDiligenceAssessmentConfigurationApi} assessmentSubject={useCaseResponse} hideModal={hideModal} moduleApi={props.ucrmApi} onDueDateSet={getUseCaseDetails} />}
            {displayedElement === PageElements.SetIrqTargetCompletionDate && <IrqTargetCompletionDateModal module={ProductModule.UCRM} hideModal={hideModal} assessmentSubject={useCaseResponse} targetDateSet={getUseCaseDetails} moduleApi={props.ucrmApi} />}

            <PageLayoutDashboard
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={generatePath(UCRM_BUSINESSES_AND_USE_CASES_LISTING)}>{ProductModule.UCRM}</BreadcrumbLink>
                        <BreadcrumbText>{useCaseTitle}</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle={useCaseTitle}
                headerButtons={
                    <>
                        <Link variant="primaryButton" to={generatePath(UCRM_UPDATE_BUSINESS, { thirdPartyId: useCaseResponse.vendor_id }) + `?${SEARCH_PARAM_USE_CASE_DASHBOARD_ORIGIN_USE_CASE_ID}=${useCaseResponse.id}&${SEARCH_PARAM_USE_CASE_DASHBOARD_ORIGIN_USE_CASE_NAME}=${encodeURIComponent(useCaseResponse.name)}`} fontAwesomeImage={ICON_EDIT_MODIFY_UPDATE}>
                            Manage Business
                        </Link>
                        <Link variant="primaryButton" to={generatePath(UCRM_UPDATE_USE_CASE, { thirdPartyId: useCaseResponse.vendor_id, serviceId: useCaseResponse.id }) + `?${SEARCH_PARAM_USE_CASE_DASHBOARD_ORIGIN_USE_CASE_ID}=${useCaseResponse.id}&${SEARCH_PARAM_USE_CASE_DASHBOARD_ORIGIN_USE_CASE_NAME}=${encodeURIComponent(useCaseResponse.name)}`} fontAwesomeImage={ICON_EDIT_MODIFY_UPDATE}>
                            Manage Use Case
                        </Link>
                    </>
                }
                dashlets={[
                    {
                        dashlets: [
                            {
                                title: 'Business and Use Case Details',
                                content: renderBusinessAndUseCaseInformationDashlet(businessResponse, useCaseResponse),
                            },
                            {
                                title: 'Due Diligence Workflow Summary',
                                content: (
                                    <div className={styles.riskAndEffectiveness}>
                                        <div>
                                            <Text variant="Text4" color="darkGray" noStyles toUppercase>
                                                Inherent Risk
                                            </Text>
                                            {inherentRisk && inherentRisk !== riskRatingAsString(RiskRating.UNRATED) ? (
                                                <div className={styles.circleIndicatorAndText}>
                                                    <CircleIndicator variant={inherentRiskVariant} />
                                                    <Text variant="Text2" color="white">
                                                        {inherentRisk}
                                                    </Text>
                                                </div>
                                            ) : (
                                                <Text variant="Text2" color="white">
                                                    -
                                                </Text>
                                            )}
                                        </div>
                                        <div>
                                            <Text variant="Text4" color="darkGray" noStyles toUppercase>
                                                Overall Control Effectiveness
                                            </Text>
                                            {useCaseResponse.control_effectiveness ? (
                                                <div className={styles.circleIndicatorAndText}>
                                                    <CircleIndicator variant={getEffectivenessVariantColor(numberAsEffectiveness(useCaseResponse.control_effectiveness))} />
                                                    <Text variant="Text2" color="white">
                                                        {numberAsEffectivenessString(useCaseResponse.control_effectiveness)}
                                                    </Text>
                                                </div>
                                            ) : (
                                                <Text variant="Text2" color="white">
                                                    -
                                                </Text>
                                            )}
                                        </div>
                                        <div>
                                            <Text variant="Text4" color="darkGray" noStyles toUppercase>
                                                Residual Risk
                                            </Text>
                                            {residualRisk && residualRiskVariant ? (
                                                <div className={styles.circleIndicatorAndText}>
                                                    <CircleIndicator variant={residualRiskVariant} />
                                                    <Text variant="Text2" color="white">
                                                        {residualRisk}
                                                    </Text>
                                                </div>
                                            ) : (
                                                <Text variant="Text2" color="white">
                                                    -
                                                </Text>
                                            )}
                                        </div>
                                        <div>
                                            <Text variant="Text4" color="darkGray" noStyles toUppercase>
                                                Inherent Risk Questionnaire Due Date
                                            </Text>
                                            <div className={styles.textAboveLinkButton}>
                                                <Text variant="Text2" color="white">
                                                    {irqTargetCompletionDateText}
                                                </Text>
                                            </div>
                                            <div className={styles.linkButton}>
                                                <Button variant="linkText" onClick={() => setDisplayedElement(PageElements.SetIrqTargetCompletionDate)} size="lg">
                                                    {useCaseResponse.assessment_workflow_setup.irq_target_completion_date ? 'Edit Inherent Risk Questionnaire Due Date' : 'Set Inherent Risk Questionnaire Due Date'}
                                                </Button>
                                            </div>
                                        </div>
                                        <div>
                                            <Text variant="Text4" color="darkGray" noStyles toUppercase>
                                                Due Diligence Workflow Due Date
                                            </Text>
                                            <div className={styles.textAboveLinkButton}>
                                                <Text variant="Text2" color="white">
                                                    {assessmentDueDateText}
                                                </Text>
                                            </div>
                                            <div className={styles.linkButton}>
                                                <Button variant="linkText" onClick={() => setDisplayedElement(PageElements.SetAssessmentDueDate)} size="lg">
                                                    {useCaseResponse.assessment_workflow_setup.due_date ? 'Edit Due Diligence Workflow Due Date' : 'Set Due Diligence Workflow Due Date'}
                                                </Button>
                                            </div>
                                        </div>
                                        <div>
                                            <Text variant="Text4" color="darkGray" noStyles toUppercase>
                                                Last Completed
                                            </Text>
                                            <Text variant="Text2" color="white">
                                                {lastAssessedDateText}
                                            </Text>
                                        </div>
                                    </div>
                                ),
                            },
                            {
                                dashlets: [
                                    {
                                        title: 'Internal Contacts',
                                        content: (
                                            <>
                                                <Text variant="Text4" color="darkGray" noStyles toUppercase>
                                                    Business Manager
                                                </Text>
                                                <Text variant="Text2" color="white">
                                                    {businessManager}
                                                </Text>
                                                <Text variant="Text4" color="darkGray" noStyles toUppercase>
                                                    Use Case Manager
                                                </Text>
                                                <Text variant="Text2" color="white">
                                                    {useCaseManager}
                                                </Text>
                                                <Text variant="Text4" color="darkGray" noStyles toUppercase>
                                                    Use Case Delegates
                                                </Text>
                                                {renderUseCaseDelegates()}
                                            </>
                                        ),
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        content:
                            useCaseResponse.assessment_workflow_data.state === AssessmentWorkflowState.AWAITING_ASSESSMENT ? (
                                <>
                                    <Text variant="Header2" color="white">
                                        Due Diligence Workflow
                                    </Text>
                                    <Button
                                        variant="linkText"
                                        onClick={() => {
                                            populateRecommendedIrqTargetCompletionDateAsDefault();
                                            setDisplayedElement(PageElements.ConfirmStartNewWorkflowModal);
                                        }}
                                        size="sm"
                                    >
                                        Begin new due diligence workflow
                                    </Button>
                                </>
                            ) : (
                                <DueDiligenceWorkflow
                                    assessmentsDisplayInfo={assessmentsDisplayInfo}
                                    module={ProductModule.UCRM}
                                    onSelectClearWorkflow={() => setDisplayedElement(PageElements.ConfirmClearWorkflowModal)}
                                    onSelectInviteRespondents={() => setDisplayedElement(PageElements.InviteRespondentsModal)}
                                    onSelectRestartWorkflow={() => {
                                        populateRecommendedIrqTargetCompletionDateAsDefault();
                                        setDisplayedElement(PageElements.ConfirmRestartWorkflowModal);
                                    }}
                                    assessmentSubject={useCaseResponse}
                                    assessmentApi={props.ucrmAssessmentApi}
                                />
                            ),
                    },
                    ...(useCaseResponse.assessment_workflow_data.state === AssessmentWorkflowState.PERFORMING_DUE_DILIGENCE
                        ? assessmentsDisplayInfo.usingCommonAssessments
                            ? [
                                  {
                                      title: 'Due Diligence Assessments',
                                      content: (
                                          <>
                                              <Text color="white">This use case is using the shared due diligence assessments provided by:</Text>
                                              <div>
                                                  <Link to={generatePath(UCRM_USE_CASE_DASHBOARD, { third_party_id: assessmentsDisplayInfo.parentSummary.vendor_id, service_id: assessmentsDisplayInfo.parentSummary.id })} size="lg">
                                                      {assessmentsDisplayInfo.parentSummary.name}
                                                  </Link>
                                              </div>
                                          </>
                                      ),
                                  },
                              ]
                            : [
                                  {
                                      title: 'Due Diligence Assessments',
                                      content: <AssessmentsListing assessments={assessmentsDisplayInfo.assessments} authHandler={props.authHandler} module={ProductModule.UCRM} pageVariant="dashboard" />,
                                  },
                              ]
                        : []),
                    {
                        title: 'Third-Party Services',
                        content: <AssociatedTprmServicesListing associatedTprmServices={useCaseResponse.associated_tprm_services} colorTheme="dark" />,
                    },
                    {
                        content: <IssuesListing {...issuesListingProps} />,
                    },
                    {
                        content: <ExceptionsListing {...exceptionsListingProps} />,
                    },
                    {
                        content: <FoldersTable {...foldersListingProps} />,
                    },
                    {
                        title: 'Due Diligence Workflow History',
                        content: (
                            <div data-testid="workflowArchivesTable">
                                <ArchivedDueDiligenceWorkflowsTable workflowArchives={workflowArchives} documentApi={props.documentApi} module={ProductModule.UCRM} onAssessmentsClicked={handleAssessmentsClicked} onFilesClicked={handleWorkflowArchiveFilesClicked} />
                            </div>
                        ),
                    },
                ]}
            />
        </>
    );
};

/** Counts the questions in an inherent risk questionnaire and how many of them are answered. Every question in a section that is not applicable counts as answered. */
const calculateInherentRiskQuestionnaireProgress = (irq: InherentRiskQuestionnaireResponse): InherentRiskQuestionnaireProgress => {
    let numberOfQuestions = 0;
    let numberOfQuestionsAnswered = 0;

    irq.sections.forEach((section) => {
        if (!section.is_applicable) {
            numberOfQuestions += section.questions.length;
            numberOfQuestionsAnswered += section.questions.length;
        } else {
            section.questions.forEach((question) => {
                numberOfQuestions++;
                if (irqQuestionIsAnswered(question)) {
                    numberOfQuestionsAnswered++;
                }
            });
        }
    });

    return {
        numberOfQuestions,
        numberOfQuestionsAnswered,
    };
};
